import { hierarchyLevelApi, userHierarchyApi } from "@/main/api";
import { KeyValue } from "@/main/models/common";
import {
	DeleteUserHierarchyRequest,
	SaveUserHierarchyRequest,
	UpdateUserHierarchyRequest,
} from "@/main/models/request";
import { UpdateHierarchyLevelRequest } from "@/main/models/request/hierarchyLevel";
import { AvailableUsersHierarchyResponse, UserHierarchyTreeResponse } from "@/main/models/response";
import { SystemApplicationEnum } from "@/main/services/enum/SystemApplicationEnum";

export type SearchHierarchyLevelsForm = {
	filter: string;
	user: null | AvailableUsersHierarchyResponse;
	levelName: string;
};

export const emptyTreeItem: UserHierarchyTreeResponse = {
	guid: "",
	userName: "",
	email: "",
	userCompanyType: undefined,
	idUser: "",
	guidUserHierarchyParent: "",
	name: "",
	guidHierarchyLevel: "",
	level: 0,
	nameHierarchyLevel: "",
	children: [],
};

export function flattenHierarchy(items: UserHierarchyTreeResponse[]): string[] {
	return items.reduce((acc: string[], item: UserHierarchyTreeResponse) => {
		acc.push(item.guid);
		if (item.children && item.children.length > 0) {
			acc = acc.concat(flattenHierarchy(item.children));
		}
		return acc;
	}, []);
}

export function filterTreeView(nodes: UserHierarchyTreeResponse[], filter: string): UserHierarchyTreeResponse[] {
	return nodes.reduce((acc: UserHierarchyTreeResponse[], node) => {
		const isMatch =
			node.userName.toLowerCase().includes(filter.toLowerCase()) ||
			node.name.toLowerCase().includes(filter.toLowerCase()) ||
			node.nameHierarchyLevel?.toLowerCase().includes(filter.toLowerCase());

		if (isMatch) {
			acc.push({ ...node });
		} else {
			const children = filterTreeView(node.children, filter);
			if (children.length > 0) {
				acc.push({ ...node, children });
			}
		}

		return acc;
	}, []);
}

export function removeNode(
	nodes: UserHierarchyTreeResponse[],
	nodeRef: UserHierarchyTreeResponse,
): UserHierarchyTreeResponse[] {
	return nodes
		.filter((x) => x.guid !== nodeRef.guid)
		.map((x) => ({
			...x,
			children: x.children ? removeNode(x.children, nodeRef) : [],
		}));
}

export async function handleGetHierarchyTree(guidInsurer: string, idSystemApplication: SystemApplicationEnum) {
	const response = await userHierarchyApi.getHierarchyTree({
		guidInsurer,
		idSystemApplication,
	});
	const resultData = response?.data?.result && response.data.result.length > 0 ? response.data.result : [];
	return resultData;
}

type TResultDeleteItem = { result: boolean; messages: KeyValue[] };
export async function handleDeleteHierarchyItem(request: DeleteUserHierarchyRequest): Promise<TResultDeleteItem> {
	try {
		await userHierarchyApi.deleteHierarchyTree(request);
		return { result: true, messages: [] };
	} catch (error: any) {
		return {
			result: false,
			messages: error.response.data.errorMessages as KeyValue[],
		};
	}
}

export async function handleSaveHierarchyItem(request: SaveUserHierarchyRequest) {
	try {
		await userHierarchyApi.saveHierarchyTree(request);
		return true;
	} catch {
		return false;
	}
}

export async function handleEditHierarchyItem(request: UpdateUserHierarchyRequest) {
	try {
		await userHierarchyApi.putHierarchyTree(request);
		return true;
	} catch {
		return false;
	}
}

export async function handleUpdateLevel(request: UpdateHierarchyLevelRequest) {
	try {
		await hierarchyLevelApi.update(request);
	} catch {
		return false;
	}
}
